import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import useGuides from '../hooks/useGuides';
import { TRADE_FAIRS } from '../data/tools';
import renderGuide from '../utils/renderGuide';
import '../styles/Dashboard.css';

const TABS = [
  { id: 'overview',  label: { en: 'Overview', fr: 'Aperçu' }, icon: '🏠' },
  { id: 'guides',    label: { en: 'All Guides', fr: 'Tous les guides' }, icon: '📚' },
  { id: 'sourcing',  label: { en: 'Wholesale Sourcing', fr: 'Achats en gros' }, icon: '📦' },
  { id: 'fairs',     label: { en: 'Trade Fairs', fr: 'Salons pro' }, icon: '🏟️' },
  { id: 'phrases',   label: { en: 'Turkish Phrases', fr: 'Phrases turques' }, icon: '🗣️' },
  { id: 'roadmap',   label: { en: 'Roadmap', fr: 'Feuille de route' }, icon: '🗺️' },
  { id: 'residence', label: { en: 'Residence & Invest', fr: 'Séjour & investir' }, icon: '🏢' },
  { id: 'profile',   label: { en: 'Profile', fr: 'Profil' }, icon: '👤' },
];

const PHRASES = [
  { q: { en: 'Hello', fr: 'Bonjour' }, a: 'Merhaba' },
  { q: { en: 'Thank you', fr: 'Merci' }, a: 'Teşekkür ederim / Teşekkürler' },
  { q: { en: 'Please', fr: 'S\'il vous plaît' }, a: 'Lütfen' },
  { q: { en: 'Yes / No', fr: 'Oui / Non' }, a: 'Evet / Hayır' },
  { q: { en: 'OK', fr: 'D\'accord' }, a: 'Tamam' },
  { q: { en: 'Sorry / Excuse me', fr: 'Pardon / Excusez-moi' }, a: 'Özür dilerim / Pardon' },
  { q: { en: 'How much is it?', fr: 'Combien ça coûte ?' }, a: 'Ne kadar?' },
  { q: { en: 'It\'s too expensive', fr: 'C\'est trop cher' }, a: 'Çok pahalı' },
  { q: { en: 'Is there a discount?', fr: 'Y a-t-il une remise ?' }, a: 'İndirim var mı?' },
  { q: { en: 'Wholesale', fr: 'En gros' }, a: 'Toptan' },
  { q: { en: 'What is the minimum order?', fr: 'Quelle est la commande minimum ?' }, a: 'Minimum sipariş ne kadar?' },
  { q: { en: 'Invoice', fr: 'Facture' }, a: 'Fatura' },
  { q: { en: 'Can you ship it by cargo?', fr: 'Pouvez-vous l\'envoyer par cargo ?' }, a: 'Kargoyla gönderebilir misiniz?' },
  { q: { en: 'The bill, please', fr: 'L\'addition, s\'il vous plaît' }, a: 'Hesap lütfen' },
  { q: { en: 'Where is …?', fr: 'Où est … ?' }, a: '… nerede?' },
  { q: { en: 'Where is the toilet?', fr: 'Où sont les toilettes ?' }, a: 'Tuvalet nerede?' },
  { q: { en: 'Airport', fr: 'Aéroport' }, a: 'Havalimanı' },
  { q: { en: 'Please use the meter', fr: 'Mettez le compteur, s\'il vous plaît' }, a: 'Taksimetreyi açar mısınız?' },
  { q: { en: 'I don\'t understand', fr: 'Je ne comprends pas' }, a: 'Anlamıyorum' },
  { q: { en: 'Help!', fr: 'Au secours !' }, a: 'İmdat! / Yardım edin!' },
];

const PASS_MARK = 12;

const MARKETS = [
  { name: 'Laleli', focus: { en: 'Women\'s clothing, evening wear, textiles', fr: 'Vêtements femme, soirée, textiles' }, access: 'T1 Laleli-Üniversite', note: { en: 'Cargo agents & trader hotels on site', fr: 'Transitaires & hôtels de commerçants sur place' } },
  { name: 'Merter', focus: { en: 'Men\'s wear, denim, children\'s wear', fr: 'Homme, denim, enfants' }, access: 'M1A Merter', note: { en: 'Dense showrooms, factories nearby', fr: 'Showrooms denses, ateliers à proximité' } },
  { name: 'Osmanbey', focus: { en: 'Women\'s fashion, modest wear', fr: 'Mode femme, mode pudique' }, access: 'M2 Osmanbey', note: { en: 'Often slightly cheaper than Laleli', fr: 'Souvent un peu moins cher que Laleli' } },
  { name: 'Mahmutpaşa / Tahtakale', focus: { en: 'Household goods, bags, small textiles', fr: 'Ménager, sacs, petits textiles' }, access: 'T1 Eminönü', note: { en: 'Retail + wholesale, very crowded', fr: 'Détail + gros, très bondé' } },
  { name: { en: 'Grand Bazaar area', fr: 'Autour du Grand Bazar' }, focus: { en: 'Gold & silver jewellery, leather', fr: 'Bijoux or & argent, cuir' }, access: 'T1 Beyazıt', note: { en: 'Always get a weight & carat invoice for gold', fr: 'Exigez une facture poids & carats pour l\'or' } },
];

const ROADMAP_STEPS = {
  tourist: [
    { step: 1, label: { en: 'Check your visa rule (visa-free / e-Visa / embassy)', fr: 'Vérifier votre règle de visa (sans visa / e-Visa / ambassade)' }, time: { en: '8 weeks before', fr: '8 semaines avant' } },
    { step: 2, label: { en: 'Book refundable flight + hotel, buy insurance', fr: 'Réserver vol + hôtel remboursables, souscrire l\'assurance' }, time: { en: '7 weeks before', fr: '7 semaines avant' } },
    { step: 3, label: { en: 'Submit visa file (bank statements, bookings)', fr: 'Déposer le dossier de visa (relevés, réservations)' }, time: { en: '6 weeks before', fr: '6 semaines avant' } },
    { step: 4, label: { en: 'Visa received — confirm bookings', fr: 'Visa reçu — confirmer les réservations' }, time: { en: '2–3 weeks before', fr: '2–3 semaines avant' } },
    { step: 5, label: { en: 'eSIM, euros in cash, itinerary chosen', fr: 'eSIM, euros en espèces, itinéraire choisi' }, time: { en: '1 week before', fr: '1 semaine avant' } },
    { step: 6, label: { en: 'Arrive: Istanbulkart + M11 or Havaist', fr: 'Arrivée : Istanbulkart + M11 ou Havaist' }, time: { en: 'Day 1', fr: 'Jour 1' } },
    { step: 7, label: { en: 'Sightseeing, Bosphorus, shopping', fr: 'Visites, Bosphore, shopping' }, time: { en: 'Days 2–6', fr: 'Jours 2–6' } },
    { step: 8, label: { en: 'Tax-free stamps at customs, then check-in (3 h before flight)', fr: 'Tampons détaxe à la douane, puis enregistrement (3 h avant le vol)' }, time: { en: 'Departure', fr: 'Départ' } },
  ],
  trader: [
    { step: 1, label: { en: 'Define products, budget and target margin at home', fr: 'Définir produits, budget et marge cible chez vous' }, time: { en: '10 weeks before', fr: '10 semaines avant' } },
    { step: 2, label: { en: 'Get an invitation letter from a Turkish supplier or fair', fr: 'Obtenir une lettre d\'invitation d\'un fournisseur ou d\'un salon' }, time: { en: '8 weeks before', fr: '8 semaines avant' } },
    { step: 3, label: { en: 'Submit business visa file', fr: 'Déposer le dossier de visa d\'affaires' }, time: { en: '6 weeks before', fr: '6 semaines avant' } },
    { step: 4, label: { en: 'Contact 2–3 cargo agents; compare price per kg / m³ door-to-door', fr: 'Contacter 2–3 transitaires ; comparer prix au kg / m³ porte-à-porte' }, time: { en: '3 weeks before', fr: '3 semaines avant' } },
    { step: 5, label: { en: 'Book hotel in Laleli or Osmanbey with cargo desk', fr: 'Réserver un hôtel à Laleli ou Osmanbey avec bureau cargo' }, time: { en: '2 weeks before', fr: '2 semaines avant' } },
    { step: 6, label: { en: 'Visit showrooms, collect samples & price lists', fr: 'Visiter les showrooms, collecter échantillons & tarifs' }, time: { en: 'Days 1–2', fr: 'Jours 1–2' } },
    { step: 7, label: { en: 'Order by series, get invoices (fatura) & packing list', fr: 'Commander par séries, obtenir factures (fatura) & liste de colisage' }, time: { en: 'Days 3–4', fr: 'Jours 3–4' } },
    { step: 8, label: { en: 'Hand goods to cargo agent, keep waybill & receipts', fr: 'Remettre au transitaire, garder lettre de transport & reçus' }, time: { en: 'Day 5', fr: 'Jour 5' } },
    { step: 9, label: { en: 'Clear customs at home, restock and plan next trip', fr: 'Dédouaner chez vous, réassort et prochain voyage' }, time: { en: '1–8 weeks later', fr: '1–8 semaines après' } },
  ],
};

const pick = (v, lang) => (typeof v === 'object' ? v[lang] || v.en : v);

const EnterpriseDashboard = () => {
  const { user, logout } = useAuth();
  const lang = localStorage.getItem('ig_language') || 'en';
  const { guides, loading: guidesLoading, error: guidesError, expired } = useGuides();
  const guideStatus = guidesLoading
    ? <p className="db-panel-note">⏳ {lang === 'fr' ? 'Chargement des guides…' : 'Loading guides…'}</p>
    : guidesError
      ? <p className="db-panel-note">⚠️ {lang === 'fr' ? 'Impossible de charger les guides. Vérifiez votre connexion et rechargez la page.' : 'Could not load guides. Check your connection and reload the page.'}</p>
      : expired
        ? <p className="db-panel-note">⌛ {lang === 'fr' ? 'Votre accès a expiré. Renouvelez votre forfait pour retrouver tous vos guides.' : 'Your access has expired. Renew your plan to get all your guides back.'}</p>
        : null;
  const learningContent = guides;

  const [tab, setTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [quizMode, setQuizMode] = useState('study');
  const [quizIndex, setQuizIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [roadmapType, setRoadmapType] = useState('tourist');

  const nextQuestion = (correct) => {
    setScore(s => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    setShowAnswer(false);
    if (quizIndex < PHRASES.length - 1) {
      setQuizIndex(i => i + 1);
    } else {
      setQuizMode('results');
    }
  };

  const restartQuiz = () => {
    setQuizIndex(0);
    setShowAnswer(false);
    setScore({ correct: 0, total: 0 });
    setQuizMode('study');
  };

  const OverviewTab = () => (
    <div className="db-overview">
      <div className="db-welcome-card enterprise">
        <div className="db-welcome-icon">🗺️</div>
        <div>
          <h2>{lang === 'fr' ? `Bienvenue, ${user?.firstName || 'Pionnier'} !` : `Welcome, ${user?.firstName || 'Pathfinder'}!`}</h2>
          <p>{lang === 'fr' ? 'Accès complet — tous les guides, sourcing en gros, séjour & investissement, phrases turques.' : 'Full access — all guides, wholesale sourcing, residence & investment, Turkish phrases.'}</p>
        </div>
        <div className="db-plan-badge enterprise">{lang === 'fr' ? 'Pionnier' : 'Pathfinder'}</div>
      </div>

      <div className="db-stats-row">
        <div className="db-stat-card">
          <div className="db-stat-num">{learningContent.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Guides Disponibles' : 'Guides Available'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">{PHRASES.length}</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Phrases turques' : 'Turkish Phrases'}</div>
        </div>
        <div className="db-stat-card">
          <div className="db-stat-num">365</div>
          <div className="db-stat-label">{lang === 'fr' ? 'Jours d\'Accès' : 'Days of Access'}</div>
        </div>
      </div>

      <div className="db-info-panel enterprise">
        <h3>📦 {lang === 'fr' ? 'Pôles de gros — en bref' : 'Wholesale Hubs — Snapshot'}</h3>
        <div className="db-fact-grid">
          <div className="db-fact"><span>Laleli</span><p>{lang === 'fr' ? 'Mode femme + transitaires' : 'Women\'s wear + cargo agents'}</p></div>
          <div className="db-fact"><span>Merter</span><p>{lang === 'fr' ? 'Homme & denim' : 'Men\'s wear & denim'}</p></div>
          <div className="db-fact"><span>Osmanbey</span><p>{lang === 'fr' ? 'Mode, souvent moins cher' : 'Fashion, often cheaper'}</p></div>
          <div className="db-fact"><span>Seri</span><p>{lang === 'fr' ? 'Vente par séries de tailles' : 'Sold in size series'}</p></div>
        </div>
      </div>

      <div className="db-info-panel green">
        <h3>🏢 {lang === 'fr' ? 'Séjour & citoyenneté — chiffres clés' : 'Residence & Citizenship — Key Figures'}</h3>
        <div className="db-fact-grid">
          <div className="db-fact"><span>$400K</span><p>{lang === 'fr' ? 'Immobilier pour la citoyenneté' : 'Property for citizenship'}</p></div>
          <div className="db-fact"><span>3 {lang === 'fr' ? 'ans' : 'yrs'}</span><p>{lang === 'fr' ? 'Durée de conservation' : 'Holding period'}</p></div>
          <div className="db-fact"><span>$200K</span><p>{lang === 'fr' ? 'Bien min. pour ikamet (Istanbul)' : 'Min. property for ikamet (Istanbul)'}</p></div>
          <div className="db-fact"><span>2 {lang === 'fr' ? 'ans' : 'yrs'}</span><p>{lang === 'fr' ? 'Max. par permis courte durée' : 'Max per short-term permit'}</p></div>
        </div>
        <button className="db-action-btn" onClick={() => setTab('residence')}>
          {lang === 'fr' ? '🏢 Voir le guide séjour & investissement' : '🏢 Open residence & investment guide'}
        </button>
      </div>

      <div className="db-features-grid">
        <div className="db-feature-card" onClick={() => setTab('sourcing')}>
          <div className="db-feature-icon">📦</div>
          <h4>{lang === 'fr' ? 'Achats en gros' : 'Wholesale Sourcing'}</h4>
          <p>{lang === 'fr' ? 'Marchés, vérification fournisseurs, cargo vers l\'Afrique' : 'Markets, supplier checks, cargo to Africa'}</p>
        </div>
        <div className="db-feature-card" onClick={() => setTab('fairs')}>
          <div className="db-feature-icon">🏟️</div>
          <h4>{lang === 'fr' ? 'Salons professionnels' : 'Trade Fairs'}</h4>
          <p>{lang === 'fr' ? 'Calendrier des salons à venir à Istanbul' : 'Upcoming Istanbul trade-fair calendar'}</p>
        </div>
        <div className="db-feature-card" onClick={() => setTab('phrases')}>
          <div className="db-feature-icon">🗣️</div>
          <h4>{lang === 'fr' ? 'Phrases turques' : 'Turkish Phrases'}</h4>
          <p>{lang === 'fr' ? '20 phrases pour les marchés, taxis et restaurants' : '20 phrases for markets, taxis and restaurants'}</p>
        </div>
        <div className="db-feature-card" onClick={() => setTab('roadmap')}>
          <div className="db-feature-icon">🗺️</div>
          <h4>{lang === 'fr' ? 'Feuille de route' : 'Trip Roadmap'}</h4>
          <p>{lang === 'fr' ? 'Voyage touristique ou d\'affaires, semaine par semaine' : 'Tourist or business trip, week by week'}</p>
        </div>
        <div className="db-feature-card" onClick={() => setTab('residence')}>
          <div className="db-feature-icon">🏢</div>
          <h4>{lang === 'fr' ? 'Séjour & investir' : 'Residence & Invest'}</h4>
          <p>{lang === 'fr' ? 'Ikamet, citoyenneté par investissement' : 'Ikamet, citizenship by investment'}</p>
        </div>
      </div>
    </div>
  );

  const GuidesTab = () => (
    <div className="db-guides">
      {selectedGuide ? (
        <div className="db-guide-reader">
          <button className="db-back-btn" onClick={() => setSelectedGuide(null)}>← {lang === 'fr' ? 'Retour' : 'Back'}</button>
          <div className="db-guide-icon-lg">{selectedGuide.icon}</div>
          <h2>{selectedGuide.title[lang] || selectedGuide.title.en}</h2>
          <div className="db-guide-content" dangerouslySetInnerHTML={{ __html: renderGuide(selectedGuide.content[lang] || selectedGuide.content.en) }} />
        </div>
      ) : (
        <>
          <h2 className="db-section-title">{lang === 'fr' ? `Bibliothèque complète — ${learningContent.length} guides` : `Complete Library — ${learningContent.length} Guides`}</h2>
          {guideStatus}
          <div className="db-guides-grid">
            {learningContent.map(guide => (
              <div key={guide.id} className="db-guide-card enterprise" onClick={() => setSelectedGuide(guide)}>
                <div className="db-guide-icon">{guide.icon}</div>
                <div className="db-guide-info">
                  <h4>{guide.title[lang] || guide.title.en}</h4>
                  <p>{guide.description[lang] || guide.description.en}</p>
                </div>
                <span className="db-guide-arrow">→</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const SourcingTab = () => (
    <div className="db-eb5">
      <h2 className="db-section-title">📦 {lang === 'fr' ? 'Module achats en gros' : 'Wholesale Sourcing Module'}</h2>

      <div className="db-table-panel">
        <h3>🏬 {lang === 'fr' ? 'Les marchés de gros d\'Istanbul' : 'Istanbul Wholesale Markets'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Quartier' : 'District'}</th><th>{lang === 'fr' ? 'Spécialité' : 'Specialty'}</th><th>{lang === 'fr' ? 'Accès' : 'Access'}</th><th>{lang === 'fr' ? 'À savoir' : 'Good to know'}</th></tr></thead>
            <tbody>
              {MARKETS.map((m, i) => (
                <tr key={i}><td><strong>{pick(m.name, lang)}</strong></td><td>{pick(m.focus, lang)}</td><td>{m.access}</td><td>{pick(m.note, lang)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'À titre informatif — vérifiez toujours vous-même chaque fournisseur.' : 'For information only — always verify each supplier yourself.'}</p>
      </div>

      <div className="db-table-panel">
        <h3>✅ {lang === 'fr' ? 'Vérification fournisseur & cargo' : 'Supplier & Cargo Due Diligence'}</h3>
        <div className="db-dd-list">
          {[
            { en: 'Ask for the company name and tax number (vergi numarası) and check they match the invoice', fr: 'Demandez la raison sociale et le numéro fiscal (vergi numarası) et vérifiez qu\'ils correspondent à la facture' },
            { en: 'Compare the same item in at least 3 showrooms before ordering', fr: 'Comparez le même article dans au moins 3 showrooms avant de commander' },
            { en: 'Check the minimum order and whether colours/sizes can be mixed in a series', fr: 'Vérifiez la commande minimum et si couleurs/tailles peuvent être mélangées' },
            { en: 'Inspect fabric, stitching and labels on a sample before paying for the full order', fr: 'Inspectez tissu, coutures et étiquettes sur un échantillon avant de payer la commande' },
            { en: 'Pay by bank transfer to a company account, or keep a signed receipt for cash', fr: 'Payez par virement sur un compte société, ou gardez un reçu signé pour les espèces' },
            { en: 'Always get an invoice (fatura) and a packing list for customs at home', fr: 'Exigez toujours une facture (fatura) et une liste de colisage pour la douane' },
            { en: 'Compare 2–3 cargo agents: price per kg (air) or m³ (sea), door-to-door or port only', fr: 'Comparez 2–3 transitaires : prix au kg (aérien) ou au m³ (maritime), porte-à-porte ou port seulement' },
            { en: 'Prefer a cargo agent with an office or partner in your city, and insure valuable goods', fr: 'Privilégiez un transitaire avec bureau ou partenaire dans votre ville, et assurez les marchandises de valeur' },
            { en: 'Check your country\'s import duties and banned items before buying', fr: 'Vérifiez les droits de douane et produits interdits de votre pays avant d\'acheter' },
            { en: 'Never buy counterfeit branded goods — they can be seized at customs', fr: 'N\'achetez jamais de contrefaçons de marques — elles peuvent être saisies à la douane' },
          ].map((item, i) => (
            <div key={i} className="db-dd-item">
              <span className="db-dd-num">{i + 1}</span>
              <span>{item[lang] || item.en}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="db-table-panel">
        <h3>✈️ {lang === 'fr' ? 'Fret aérien ou maritime ?' : 'Air or Sea Freight?'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Critère' : 'Factor'}</th><th>{lang === 'fr' ? 'Aérien' : 'Air'}</th><th>{lang === 'fr' ? 'Maritime' : 'Sea'}</th></tr></thead>
            <tbody>
              <tr><td>{lang === 'fr' ? 'Délai' : 'Transit time'}</td><td>{lang === 'fr' ? 'Quelques jours' : 'A few days'}</td><td>{lang === 'fr' ? '4–8 semaines' : '4–8 weeks'}</td></tr>
              <tr><td>{lang === 'fr' ? 'Tarification' : 'Pricing'}</td><td>{lang === 'fr' ? 'Au kg' : 'Per kg'}</td><td>{lang === 'fr' ? 'Au m³ ou conteneur' : 'Per m³ or container'}</td></tr>
              <tr><td>{lang === 'fr' ? 'Idéal pour' : 'Best for'}</td><td>{lang === 'fr' ? 'Mode, petits volumes, urgences' : 'Fashion, small volumes, urgent stock'}</td><td>{lang === 'fr' ? 'Gros volumes, articles lourds' : 'Large volumes, heavy goods'}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const FairsTab = () => {
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = TRADE_FAIRS.filter(f => f.end >= today);
    const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return (
      <div className="db-eb5">
        <h2 className="db-section-title">🏟️ {lang === 'fr' ? 'Calendrier des salons à Istanbul' : 'Istanbul Trade Fair Calendar'}</h2>
        <p className="db-section-subtitle">{lang === 'fr' ? 'Salons à venir (calendriers officiels TÜYAP et IFM). ⭐ = particulièrement utile aux commerçants africains.' : 'Upcoming fairs (official TÜYAP and IFM calendars). ⭐ = especially useful for African traders.'}</p>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Dates' : 'Dates'}</th><th>{lang === 'fr' ? 'Salon' : 'Fair'}</th><th>{lang === 'fr' ? 'Secteur' : 'Sector'}</th><th>{lang === 'fr' ? 'Lieu' : 'Venue'}</th></tr></thead>
            <tbody>
              {upcoming.map((f, i) => (
                <tr key={i}>
                  <td>{fmtDate(f.start)} – {fmtDate(f.end)}</td>
                  <td><strong>{f.hot ? '⭐ ' : ''}{f.name}</strong></td>
                  <td>{f.sector[lang] || f.sector.en}</td>
                  <td>{f.venue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {upcoming.length === 0 && <p className="db-panel-note">{lang === 'fr' ? 'Calendrier en cours de mise à jour.' : 'Calendar being updated.'}</p>}
        <div className="db-bulletin-note">
          <strong>📌 {lang === 'fr' ? 'Avant de réserver' : 'Before you book'}:</strong>
          <ol>
            <li>{lang === 'fr' ? 'Confirmez les dates sur tuyap.com.tr ou ifm.com.tr' : 'Confirm dates on tuyap.com.tr or ifm.com.tr'}</li>
            <li>{lang === 'fr' ? 'Inscrivez-vous comme visiteur et demandez une lettre d\'invitation pour le visa' : 'Register as a visitor and request an invitation letter for your visa'}</li>
            <li>{lang === 'fr' ? 'Réservez l\'hôtel tôt — les prix montent pendant les salons' : 'Book your hotel early — prices rise during fairs'}</li>
            <li>{lang === 'fr' ? 'Voir le guide « Salons professionnels » pour la méthode complète' : 'See the "Trade Fairs" guide for the full method'}</li>
          </ol>
        </div>
      </div>
    );
  };

  const PhrasesTab = () => {
    if (quizMode === 'results') {
      return (
        <div className="db-civics">
          <div className="db-civics-result">
            <div className="db-civics-score-icon">{score.correct >= PASS_MARK ? '🎉' : '📚'}</div>
            <h2>{lang === 'fr' ? 'Résultats' : 'Results'}</h2>
            <div className="db-civics-score">{score.correct}/{score.total}</div>
            <p>{score.correct >= PASS_MARK ? (lang === 'fr' ? 'Bravo ! Vous êtes prêt pour les marchés d\'Istanbul.' : 'Well done! You\'re ready for Istanbul\'s markets.') : (lang === 'fr' ? `Continuez à pratiquer — visez ${PASS_MARK}/${PHRASES.length}.` : `Keep practising — aim for ${PASS_MARK}/${PHRASES.length}.`)}</p>
            <button className="db-action-btn" onClick={restartQuiz}>{lang === 'fr' ? '🔄 Recommencer' : '🔄 Try Again'}</button>
          </div>
        </div>
      );
    }

    const current = PHRASES[quizIndex];
    return (
      <div className="db-civics">
        <h2 className="db-section-title">🗣️ {lang === 'fr' ? `Phrases turques — ${quizIndex + 1}/${PHRASES.length}` : `Turkish Phrases — ${quizIndex + 1}/${PHRASES.length}`}</h2>
        <div className="db-civics-progress">
          <div className="db-progress-bar">
            <div className="db-progress-fill enterprise" style={{ width: `${((quizIndex + 1) / PHRASES.length) * 100}%` }} />
          </div>
        </div>
        <div className="db-civics-card">
          <div className="db-civics-q">{lang === 'fr' ? 'Comment dit-on en turc :' : 'How do you say in Turkish:'} « {current.q[lang] || current.q.en} »</div>
          {!showAnswer ? (
            <button className="db-action-btn" onClick={() => setShowAnswer(true)}>
              {lang === 'fr' ? '👁️ Voir la réponse' : '👁️ Show Answer'}
            </button>
          ) : (
            <div className="db-civics-answer">
              <div className="db-civics-a-label">{lang === 'fr' ? 'En turc :' : 'In Turkish:'}</div>
              <div className="db-civics-a">{current.a}</div>
              <div className="db-civics-actions">
                <button className="db-civics-correct" onClick={() => nextQuestion(true)}>✅ {lang === 'fr' ? 'Je savais' : 'I knew it'}</button>
                <button className="db-civics-wrong" onClick={() => nextQuestion(false)}>❌ {lang === 'fr' ? 'Je ne savais pas' : 'I didn\'t know'}</button>
              </div>
            </div>
          )}
        </div>
        <div className="db-civics-progress-text">{lang === 'fr' ? `Score : ${score.correct}/${score.total}` : `Score: ${score.correct}/${score.total}`}</div>
      </div>
    );
  };

  const RoadmapTab = () => (
    <div className="db-roadmap">
      <h2 className="db-section-title">🗺️ {lang === 'fr' ? 'Feuille de route du voyage' : 'Trip Roadmap'}</h2>
      <div className="db-roadmap-tabs">
        <button className={`db-rm-tab ${roadmapType === 'tourist' ? 'active' : ''}`} onClick={() => setRoadmapType('tourist')}>{lang === 'fr' ? '🕌 Voyage touristique' : '🕌 Tourist trip'}</button>
        <button className={`db-rm-tab ${roadmapType === 'trader' ? 'active' : ''}`} onClick={() => setRoadmapType('trader')}>{lang === 'fr' ? '📦 Voyage d\'affaires / achats' : '📦 Business / sourcing trip'}</button>
      </div>
      <div className="db-roadmap-steps">
        {ROADMAP_STEPS[roadmapType].map((s, i) => (
          <div key={i} className="db-rm-step">
            <div className="db-rm-num">{s.step}</div>
            <div className="db-rm-content">
              <div className="db-rm-label">{s.label[lang] || s.label.en}</div>
              <div className="db-rm-time">{s.time[lang] || s.time.en}</div>
            </div>
            {i < ROADMAP_STEPS[roadmapType].length - 1 && <div className="db-rm-connector" />}
          </div>
        ))}
      </div>
    </div>
  );

  const ResidenceTab = () => (
    <div className="db-investor">
      <h2 className="db-section-title">🏢 {lang === 'fr' ? 'Séjour & investissement en Turquie' : 'Residence & Investment in Türkiye'}</h2>

      <div className="db-table-panel">
        <h3>{lang === 'fr' ? 'Types de permis de séjour (ikamet)' : 'Residence Permit Types (ikamet)'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Type' : 'Type'}</th><th>{lang === 'fr' ? 'Pour qui' : 'For whom'}</th><th>{lang === 'fr' ? 'Points clés' : 'Key points'}</th></tr></thead>
            <tbody>
              <tr><td><strong>{lang === 'fr' ? 'Courte durée' : 'Short-term'}</strong></td><td>{lang === 'fr' ? 'Tourisme, affaires, propriétaires' : 'Tourism, business, property owners'}</td><td>{lang === 'fr' ? 'Jusqu\'à 2 ans ; assurance santé obligatoire' : 'Up to 2 years; health insurance required'}</td></tr>
              <tr><td><strong>{lang === 'fr' ? 'Étudiant' : 'Student'}</strong></td><td>{lang === 'fr' ? 'Étudiants inscrits' : 'Enrolled students'}</td><td>{lang === 'fr' ? 'Demande dans les 30 jours après l\'arrivée' : 'Apply within 30 days of arrival'}</td></tr>
              <tr><td><strong>{lang === 'fr' ? 'Familial' : 'Family'}</strong></td><td>{lang === 'fr' ? 'Conjoint / enfants de résidents' : 'Spouse / children of residents'}</td><td>{lang === 'fr' ? 'Le garant justifie revenus et logement' : 'Sponsor shows income and housing'}</td></tr>
              <tr><td><strong>{lang === 'fr' ? 'Longue durée' : 'Long-term'}</strong></td><td>{lang === 'fr' ? 'Après 8 ans de résidence continue' : 'After 8 years of continuous residence'}</td><td>{lang === 'fr' ? 'Statut permanent' : 'Permanent status'}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'Demande sur e-ikamet.goc.gov.tr puis rendez-vous au bureau de l\'immigration. Budget indicatif premier permis 1 an : ~15 000 TL (frais, carte, assurance).' : 'Apply at e-ikamet.goc.gov.tr, then attend the migration office appointment. Indicative budget for a first 1-year permit: ~15,000 TL (fees, card, insurance).'}</p>
      </div>

      <div className="db-table-panel">
        <h3>{lang === 'fr' ? 'Citoyenneté turque par investissement' : 'Turkish Citizenship by Investment'}</h3>
        <div className="db-table-wrap">
          <table className="db-table">
            <thead><tr><th>{lang === 'fr' ? 'Voie' : 'Route'}</th><th>{lang === 'fr' ? 'Minimum' : 'Minimum'}</th><th>{lang === 'fr' ? 'Conservation' : 'Holding'}</th></tr></thead>
            <tbody>
              <tr><td><strong>{lang === 'fr' ? 'Immobilier' : 'Real estate'}</strong></td><td>$400,000</td><td>3 {lang === 'fr' ? 'ans' : 'years'}</td></tr>
              <tr><td>{lang === 'fr' ? 'Dépôt bancaire' : 'Bank deposit'}</td><td>$500,000</td><td>3 {lang === 'fr' ? 'ans' : 'years'}</td></tr>
              <tr><td>{lang === 'fr' ? 'Capital fixe' : 'Fixed capital'}</td><td>$500,000</td><td>—</td></tr>
              <tr><td>{lang === 'fr' ? 'Obligations / fonds d\'État' : 'Government bonds / funds'}</td><td>$500,000</td><td>3 {lang === 'fr' ? 'ans' : 'years'}</td></tr>
              <tr><td>{lang === 'fr' ? 'Création d\'emplois' : 'Job creation'}</td><td>50 {lang === 'fr' ? 'salariés' : 'employees'}</td><td>—</td></tr>
            </tbody>
          </table>
        </div>
        <p className="db-panel-note">{lang === 'fr' ? 'Délai habituel 6–12 mois, pas de résidence ni de test de langue, famille incluse. Information générale, pas un conseil juridique — consultez un avocat turc agréé.' : 'Typical processing 6–12 months, no residency or language test, family included. General information, not legal advice — consult a licensed Turkish lawyer.'}</p>
      </div>

      <div className="db-table-panel">
        <h3>{lang === 'fr' ? 'Produits populaires pour la revente en Afrique' : 'Popular Products for Resale in Africa'}</h3>
        <div className="db-business-ideas">
          {[
            { icon: '👗', en: 'Women\'s fashion & evening wear (Laleli, Osmanbey)', fr: 'Mode femme & tenues de soirée (Laleli, Osmanbey)' },
            { icon: '👖', en: 'Denim & men\'s wear (Merter)', fr: 'Denim & vêtements homme (Merter)' },
            { icon: '🧕', en: 'Modest fashion, abayas, scarves', fr: 'Mode pudique, abayas, foulards' },
            { icon: '👶', en: 'Children\'s clothing', fr: 'Vêtements enfants' },
            { icon: '🛏️', en: 'Home textiles: towels, bedding, curtains', fr: 'Linge de maison : serviettes, draps, rideaux' },
            { icon: '👞', en: 'Shoes and leather bags', fr: 'Chaussures et sacs en cuir' },
            { icon: '🍯', en: 'Turkish delight, dried fruit, tea (check import rules)', fr: 'Loukoum, fruits secs, thé (vérifiez les règles d\'import)' },
            { icon: '🍳', en: 'Kitchenware and household goods', fr: 'Ustensiles de cuisine et articles ménagers' },
          ].map((idea, i) => (
            <div key={i} className="db-business-idea">
              <span>{idea.icon}</span>
              <span>{idea[lang] || idea.en}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ProfileTab = () => (
    <div className="db-profile">
      <div className="db-profile-card enterprise">
        <div className="db-profile-avatar">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
        <h2>{user?.firstName} {user?.lastName}</h2>
        <p>{user?.email}</p>
        <div className="db-plan-badge enterprise">{lang === 'fr' ? 'Forfait Pionnier' : 'Pathfinder Plan'}</div>
      </div>
      <div className="db-profile-details">
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Pays d\'origine' : 'Country of origin'}</span><strong>{user?.country || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Numéro de téléphone' : 'Phone number'}</span><strong>{user?.phone || '—'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Forfait actuel' : 'Current plan'}</span><strong>{lang === 'fr' ? 'Pionnier (Entreprise)' : 'Pathfinder (Enterprise)'}</strong></div>
        <div className="db-detail-row"><span>{lang === 'fr' ? 'Accès' : 'Access'}</span><strong>{lang === 'fr' ? '1 an complet' : '1 full year'}</strong></div>
      </div>
      <div className="db-profile-actions">
        <button className="db-logout-btn" onClick={logout}>{lang === 'fr' ? 'Se Déconnecter' : 'Sign Out'}</button>
      </div>
    </div>
  );

  const renderTab = () => {
    switch (tab) {
      case 'overview':  return <OverviewTab />;
      case 'guides':    return <GuidesTab />;
      case 'sourcing':  return <SourcingTab />;
      case 'fairs':     return <FairsTab />;
      case 'phrases':   return <PhrasesTab />;
      case 'roadmap':   return <RoadmapTab />;
      case 'residence': return <ResidenceTab />;
      case 'profile':   return <ProfileTab />;
      default:          return <OverviewTab />;
    }
  };

  return (
    <div className="db-layout">
      <aside className="db-sidebar enterprise">
        <div className="db-sidebar-brand">
          <span className="db-brand-icon">🕌</span>
          <span className="db-brand-text">{lang === 'fr' ? 'Guide Istanbul' : 'Istanbul Guide'}</span>
        </div>
        <nav className="db-nav">
          {TABS.map(t => (
            <button key={t.id} className={`db-nav-item ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span className="db-nav-icon">{t.icon}</span>
              <span>{t.label[lang] || t.label.en}</span>
            </button>
          ))}
        </nav>
        <div className="db-sidebar-footer">
          <button className="db-signout-btn" onClick={logout}>{lang === 'fr' ? 'Déconnexion' : 'Sign Out'}</button>
        </div>
      </aside>
      <main className="db-main">
        <div className="db-topbar">
          <h1 className="db-page-title">
            {TABS.find(t => t.id === tab)?.icon} {TABS.find(t => t.id === tab)?.label[lang]}
          </h1>
          <div className="db-user-chip enterprise">
            <div className="db-user-avatar">{user?.firstName?.[0]}</div>
            <span>{user?.firstName}</span>
          </div>
        </div>
        <div className="db-content">
          {renderTab()}
        </div>
      </main>
    </div>
  );
};

export default EnterpriseDashboard;
